import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, GraduationCap, Sparkles } from 'lucide-react';
import authService from '../../services/api/auth';
import { useForm } from 'react-hook-form';
import { useToast } from '../../components/common/Toast';

const ForgotPassword = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [serverMessage, setServerMessage] = useState('');

  const onSubmit = async (data) => {
    setIsLoading(true);
    setServerMessage('');

    try {
      const res = await authService.forgotPassword({ email: data.email });
      setServerMessage(res?.message || 'إذا كان البريد مسجلاً لدينا، ستصلك رسالة خلال دقائق.');
      toast.success('تم إرسال التعليمات إن كان البريد مسجلاً.');
    } catch (error) {
      // نُظهر رسالة عامة فقط لأسباب أمان
      const msg = error.response?.data?.error || 'إذا كان البريد مسجلاً لدينا، ستصلك رسالة خلال دقائق.';
      setServerMessage(msg);
      toast.info(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/60 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10" />

      <div className="max-w-md mx-auto w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* Branding Header */}
        <div className="text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="bg-primary-600 p-4 rounded-[1.5rem] shadow-xl shadow-primary-200 transform group-hover:rotate-12 transition-all duration-500">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">مـنـصـتـي</h1>
              <p className="text-primary-600 text-xs font-black uppercase tracking-[0.2em] mt-1">
                استعادة الوصول إلى حسابك
              </p>
            </div>
          </Link>
          <div className="space-y-1">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">نسيت كلمة المرور؟</h2>
            <p className="text-gray-500 font-medium">
              أدخل بريدك الإلكتروني وسنرسل لك رابطًا لإعادة تعيين كلمة المرور.
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 p-8 lg:p-10 border border-gray-100">
          {serverMessage && (
            <div className="mb-6 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
              {serverMessage}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                البريد الإلكتروني المسجَّل
              </label>
              <div className="relative group">
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                <input
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'تنسيق البريد الإلكتروني غير صحيح',
                    },
                  })}
                  type="email"
                  autoComplete="email"
                  placeholder="your@email.com"
                  className={`
                    w-full pr-12 pl-4 py-4 bg-gray-50 border rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold
                    ${errors.email ? 'border-red-300' : 'border-gray-100 focus:border-primary-500'}
                  `}
                />
              </div>
              {errors.email && (
                <p className="mt-1 mr-2 text-[10px] text-red-500 font-black flex items-center gap-1">
                  <Sparkles size={10} /> {errors.email.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all active:scale-95 disabled:opacity-60"
            >
              {isLoading ? 'جارٍ الإرسال...' : 'إرسال رابط إعادة التعيين'}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs font-black text-gray-500">
            <Link to="/login" className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
              <ArrowLeft size={14} /> العودة لتسجيل الدخول
            </Link>
            <Link to="/register" className="hover:text-primary-600 transition-colors">
              إنشاء حساب جديد
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

